import { CANADA_DATA, type Province } from '../data/CanadianLocations';
import { SERVICE_ORDER_SUBCATEGORIES } from '../data/serviceOrderSubcategories';
import { categoryKeyToPath } from './pathUtils';
import { getEnhancedLocalLandingMeta } from './localSeoLandingSeoEnhancer';

/** Canonical public prefix for local SEO landings (must match Laravel sitemap). */
export const LOCAL_SEO_PATH_PREFIX = '/locations';

/** URL slug for a city or subcategory label (aligned with PHP `Str::slug()` for sitemap URLs). */
export function cityNameToLocalSeoSlug(name: string): string {
    return name
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[''`]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

export const subcategoryNameToSlug = cityNameToLocalSeoSlug;

/**
 * Cities included in the XML sitemap for `/locations/...` URLs only. Routing still accepts every city in {@link CANADA_DATA}.
 * Must stay aligned with {@see LocalSeoPathBuilder} (PHP).
 */
export const LOCAL_SEO_LANDING_CITY_NAMES = [
    'Toronto',
    'Mississauga',
    'Brampton',
    'Hamilton',
    'Montreal',
    'Ottawa',
    'Calgary',
    'Edmonton',
    'Winnipeg',
    'Vancouver',
    'Guelph',
    'Welland',
    'Terrebonne',
    'Brossard',
    'Milton',
    'Markham',
    'Kitchener',
    'Cambridge',
    'Halifax',
    'Pickering',
    'Newmarket',
    'Barrie',
    'Whitby',
    'Nanaimo',
    'Timmins',
    'Langley',
    'Lethbridge',
    'Vaughan',
    'Oakville',
    'Ajax',
    'Lévis',
    'Aurora',
    'Laval',
    'Windsor',
    'Burlington',
    'Mascouche',
    'Saint-Jean-sur-Richelieu',
    'Waterloo',
    'Oshawa',
    // High-impression / click cities from Search Console (expand crawl discovery)
    'London',
    'Surrey',
    'Richmond',
    'Gatineau',
    'Longueuil',
    'Pitt Meadows',
    'Chilliwack',
    'Moose Jaw',
    'Iqaluit',
    'Gander',
    'Truro',
    'Brantford',
    'Peterborough',
    'Vernon',
    'Coquitlam',
    'Estevan',
    'Selkirk',
    'St. Albert',
    'Cold Lake',
    'Comox',
    'Rimouski',
    'Steinbach',
    'Winkler',
    'Saguenay',
    'Quebec City',
    'Trois-Rivières',
    'Kingston',
    "St. John's",
    'Dieppe',
    'Whitehorse',
    'Yellowknife',
    'Leduc',
    'Nisku',
    'Fort McMurray',
    'Okotoks',
    'Medicine Hat',
    'Yorkton',
    'Brandon',
    'Delta',
    'Victoria',
    'Kelowna',
    'Regina',
    'Saskatoon',
    'Red Deer',
    'Grande Prairie',
    'Airdrie',
    'Thunder Bay',
    'Sarnia',
    'Niagara Falls',
    'St. Catharines',
    'Dartmouth',
    'Fredericton',
    'Charlottetown',
    'New Glasgow',
    'Sydney',
    'Portage la Prairie',
    'Prince George',
    'Fort St. John',
    'Courtenay',
    'Saint-Jérôme',
    'Sherbrooke',
    'Norfolk County (Simcoe)',
] as const;

const LOCAL_SEO_LANDING_CITY_NAME_SET: ReadonlySet<string> = new Set(
    LOCAL_SEO_LANDING_CITY_NAMES.map((n) => n.toLowerCase()),
);

export function isLocalSeoLandingCity(cityName: string): boolean {
    return LOCAL_SEO_LANDING_CITY_NAME_SET.has(cityName.trim().toLowerCase());
}

/**
 * Customer-service path segments for sitemap `/locations/...` and `/locations/.../join/...` only (subset of categories).
 * Keep aligned with {@see LocalSeoPathBuilder} `SITEMAP_CUSTOMER_SERVICE_SLUGS`.
 */
export const LOCAL_SEO_SITEMAP_CUSTOMER_SERVICE_SLUGS = [
    'cleaning-services',
    'handyman-and-repairs',
    'seasonal-and-outdoor-maintenance',
    'home-improvement-and-renovation',
    'auto-services',
    'assembly-and-installation',
    'moving-and-hauling',
    'shopping-and-delivery',
    'pet-care',
    'property-and-environmental-services',
    'home-safety-and-security',
    'exterior-and-structural',
    'tech-and-electronics',
    'clothing-alteration-and-tailoring',
    'pool-and-spa-services',
    'education-and-lessons',
    'health-wellness-and-care',
    'legal-and-financial-services',
    'office-and-workspace',
    'event-services',
] as const;

const LOCAL_SEO_SITEMAP_CUSTOMER_SLUG_SET: ReadonlySet<string> = new Set(LOCAL_SEO_SITEMAP_CUSTOMER_SERVICE_SLUGS);

function isLocalSeoSitemapCategory(def: LocalSeoCategoryDefinition): boolean {
    return LOCAL_SEO_SITEMAP_CUSTOMER_SLUG_SET.has(def.customerServiceSlug);
}

export interface LocalSeoCategoryDefinition {
    /** Short segment in /locations/{prov}/city/{slug} */
    slug: string;
    /** `customer-service/{this}` path segment */
    customerServiceSlug: string;
    /** Lowercase phrase used in H1 after city, e.g. "home cleaning" → "Winnipeg home cleaning" */
    seoServicePhrase: string;
    /** Section heading / title fragment */
    serviceHeading: string;
}

export const LOCAL_SEO_CATEGORY_DEFINITIONS: readonly LocalSeoCategoryDefinition[] = [
    {
        slug: 'cleaning',
        customerServiceSlug: 'cleaning-services',
        seoServicePhrase: 'home cleaning',
        serviceHeading: 'Home cleaning',
    },
    {
        slug: 'handyman',
        customerServiceSlug: 'handyman-and-repairs',
        seoServicePhrase: 'handyman services',
        serviceHeading: 'Handyman & repairs',
    },
    {
        slug: 'landscaping',
        customerServiceSlug: 'seasonal-and-outdoor-maintenance',
        seoServicePhrase: 'landscaping & outdoor services',
        serviceHeading: 'Landscaping & outdoor',
    },
    {
        slug: 'home-improvement',
        customerServiceSlug: 'home-improvement-and-renovation',
        seoServicePhrase: 'home improvement',
        serviceHeading: 'Home improvement',
    },
    {
        slug: 'auto',
        customerServiceSlug: 'auto-services',
        seoServicePhrase: 'auto services',
        serviceHeading: 'Auto services',
    },
    {
        slug: 'assembly',
        customerServiceSlug: 'assembly-and-installation',
        seoServicePhrase: 'assembly & installation',
        serviceHeading: 'Assembly & installation',
    },
    {
        slug: 'tech',
        customerServiceSlug: 'tech-and-electronics',
        seoServicePhrase: 'tech & electronics help',
        serviceHeading: 'Tech & electronics',
    },
    {
        slug: 'wellness',
        customerServiceSlug: 'health-wellness-and-care',
        seoServicePhrase: 'health & wellness care',
        serviceHeading: 'Health & wellness',
    },
    {
        slug: 'moving',
        customerServiceSlug: 'moving-and-hauling',
        seoServicePhrase: 'moving & hauling',
        serviceHeading: 'Moving & hauling',
    },
    {
        slug: 'delivery',
        customerServiceSlug: 'shopping-and-delivery',
        seoServicePhrase: 'shopping & delivery',
        serviceHeading: 'Shopping & delivery',
    },
    {
        slug: 'pet-care',
        customerServiceSlug: 'pet-care',
        seoServicePhrase: 'pet care',
        serviceHeading: 'Pet care',
    },
    {
        slug: 'events',
        customerServiceSlug: 'event-services',
        seoServicePhrase: 'event services',
        serviceHeading: 'Event services',
    },
    {
        slug: 'security',
        customerServiceSlug: 'home-safety-and-security',
        seoServicePhrase: 'home safety & security',
        serviceHeading: 'Home safety & security',
    },
    {
        slug: 'tailoring',
        customerServiceSlug: 'clothing-alteration-and-tailoring',
        seoServicePhrase: 'tailoring & alterations',
        serviceHeading: 'Tailoring & alterations',
    },
    {
        slug: 'office',
        customerServiceSlug: 'office-and-workspace',
        seoServicePhrase: 'office & workspace',
        serviceHeading: 'Office & workspace',
    },
    {
        slug: 'legal',
        customerServiceSlug: 'legal-and-financial-services',
        seoServicePhrase: 'legal & financial services',
        serviceHeading: 'Legal & financial',
    },
    {
        slug: 'lessons',
        customerServiceSlug: 'education-and-lessons',
        seoServicePhrase: 'lessons & tutoring',
        serviceHeading: 'Education & lessons',
    },
    {
        slug: 'property',
        customerServiceSlug: 'property-and-environmental-services',
        seoServicePhrase: 'property & environmental services',
        serviceHeading: 'Property & environmental',
    },
    {
        slug: 'pool',
        customerServiceSlug: 'pool-and-spa-services',
        seoServicePhrase: 'pool & spa services',
        serviceHeading: 'Pool & spa',
    },
    {
        slug: 'exterior',
        customerServiceSlug: 'exterior-and-structural',
        seoServicePhrase: 'exterior & structural work',
        serviceHeading: 'Exterior & structural',
    },
] as const;

/**
 * Customer-service URL segments for `/customer-service/{slug}` and `/locations/.../join/{slug}`.
 * Single source: {@link LOCAL_SEO_CATEGORY_DEFINITIONS} — add or change categories only there.
 */
export const LOCAL_SEO_CUSTOMER_SERVICE_SLUGS: readonly string[] = LOCAL_SEO_CATEGORY_DEFINITIONS.map(
    (c) => c.customerServiceSlug,
);

const CATEGORY_BY_SLUG: ReadonlyMap<string, LocalSeoCategoryDefinition> = new Map(
    LOCAL_SEO_CATEGORY_DEFINITIONS.map((c) => [c.slug, c]),
);

const VALID_PROVINCE_CODES = new Set(CANADA_DATA.provinces.map((p) => p.code.toLowerCase()));

export function getLocalSeoCategoryBySlug(slug: string): LocalSeoCategoryDefinition | undefined {
    return CATEGORY_BY_SLUG.get(slug.toLowerCase());
}

/** Service order config category display name for this local SEO bucket (subcategories list). */
export function serviceOrderCategoryNameForLocalCategory(def: LocalSeoCategoryDefinition): string | undefined {
    const target = def.customerServiceSlug;
    for (const name of Object.keys(SERVICE_ORDER_SUBCATEGORIES)) {
        if (categoryKeyToPath(name) === target) {
            return name;
        }
    }
    return undefined;
}

/** Same labels as backend / `SERVICE_ORDER_SUBCATEGORIES` keys (e.g. "Cleaning Services"). */
export function localCategoryDisplayName(def: LocalSeoCategoryDefinition): string {
    return serviceOrderCategoryNameForLocalCategory(def) ?? def.serviceHeading;
}

export interface LocalLandingResolved {
    provinceCode: string;
    provinceName: string;
    cityName: string;
    citySlug: string;
    category: LocalSeoCategoryDefinition;
    subcategory: { name: string; slug: string } | null;
}

function findCityInProvince(province: Province, citySlug: string): string | null {
    const want = citySlug.toLowerCase();
    for (const city of province.cities) {
        if (cityNameToLocalSeoSlug(city) === want) {
            return city;
        }
    }
    return null;
}

export function resolveLocalLanding(
    provinceParam: string,
    citySlug: string,
    categorySlug: string,
    optionalSubSlug?: string | null,
): LocalLandingResolved | null {
    const codeUpper = provinceParam.toUpperCase();
    if (codeUpper.length !== 2 || !VALID_PROVINCE_CODES.has(provinceParam.toLowerCase())) {
        return null;
    }
    const province = CANADA_DATA.provinces.find((p) => p.code === codeUpper);
    if (!province) {
        return null;
    }
    const cityName = findCityInProvince(province, citySlug);
    if (!cityName) {
        return null;
    }
    const category = getLocalSeoCategoryBySlug(categorySlug);
    if (!category) {
        return null;
    }
    const base: Omit<LocalLandingResolved, 'subcategory'> = {
        provinceCode: province.code,
        provinceName: province.name,
        cityName,
        citySlug: cityNameToLocalSeoSlug(cityName),
        category,
    };
    const trimmedSub = optionalSubSlug?.trim();
    if (!trimmedSub) {
        return { ...base, subcategory: null };
    }
    const orderName = serviceOrderCategoryNameForLocalCategory(category);
    if (!orderName) {
        return null;
    }
    const subs = SERVICE_ORDER_SUBCATEGORIES[orderName];
    if (!subs?.length) {
        return null;
    }
    const want = trimmedSub.toLowerCase();
    const subName = subs.find((s) => subcategoryNameToSlug(s) === want);
    if (!subName) {
        return null;
    }
    return {
        ...base,
        subcategory: { name: subName, slug: subcategoryNameToSlug(subName) },
    };
}

export function resolveLocalLandingFromPathname(pathname: string): LocalLandingResolved | null {
    const normalized = pathname.replace(/\/$/, '') || '/';
    const withLoc = normalized.match(
        /^\/locations\/([a-z]{2})\/([^/]+)\/([^/]+)\/([^/]+)$/i,
    );
    if (withLoc) {
        return resolveLocalLanding(withLoc[1], withLoc[2], withLoc[3], withLoc[4]);
    }
    const baseOnly = normalized.match(/^\/locations\/([a-z]{2})\/([^/]+)\/([^/]+)$/i);
    if (baseOnly) {
        return resolveLocalLanding(baseOnly[1], baseOnly[2], baseOnly[3], null);
    }
    return null;
}

export function localLandingPath(resolved: LocalLandingResolved): string {
    const base = `${LOCAL_SEO_PATH_PREFIX}/${resolved.provinceCode.toLowerCase()}/${resolved.citySlug}/${resolved.category.slug}`;
    if (resolved.subcategory) {
        return `${base}/${resolved.subcategory.slug}`;
    }
    return base;
}

export function localLandingCustomerServicePath(resolved: LocalLandingResolved): string {
    const q = resolved.subcategory
        ? `?subcategory=${encodeURIComponent(resolved.subcategory.name)}`
        : '';
    return `/customer-service/${resolved.category.customerServiceSlug}${q}`;
}

/** Expert acquisition landing: `/locations/{prov}/{city}/join/{categorySlug}` */
export interface WorkerJoinLandingResolved {
    provinceCode: string;
    provinceName: string;
    cityName: string;
    citySlug: string;
    category: LocalSeoCategoryDefinition;
}

/** Alternate URL spellings from ads → canonical `customerServiceSlug`. */
const JOIN_CATEGORY_ALIASES: Readonly<Record<string, string>> = {
    'home-improvement-renovation': 'home-improvement-and-renovation',
    'home-improvement-renovations': 'home-improvement-and-renovation',
};

/** Normalize last path segment (any casing, separators) for lookup. */
export function normalizeJoinCategorySegment(segment: string): string {
    return segment
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/** Resolve category from join URL segment (short slug, customer-service slug, or aliases). */
export function getLocalSeoCategoryFromJoinSegment(segment: string): LocalSeoCategoryDefinition | undefined {
    const n = normalizeJoinCategorySegment(segment);
    if (!n) {
        return undefined;
    }
    const byShort = getLocalSeoCategoryBySlug(n);
    if (byShort) {
        return byShort;
    }
    const customerSlug = JOIN_CATEGORY_ALIASES[n] ?? n;
    for (const def of LOCAL_SEO_CATEGORY_DEFINITIONS) {
        if (def.customerServiceSlug === customerSlug) {
            return def;
        }
    }
    return undefined;
}

export function resolveWorkerJoinLanding(
    provinceParam: string,
    citySlug: string,
    joinCategorySegment: string,
): WorkerJoinLandingResolved | null {
    const codeUpper = provinceParam.toUpperCase();
    if (codeUpper.length !== 2 || !VALID_PROVINCE_CODES.has(provinceParam.toLowerCase())) {
        return null;
    }
    const province = CANADA_DATA.provinces.find((p) => p.code === codeUpper);
    if (!province) {
        return null;
    }
    const cityName = findCityInProvince(province, citySlug);
    if (!cityName) {
        return null;
    }
    const category = getLocalSeoCategoryFromJoinSegment(joinCategorySegment);
    if (!category) {
        return null;
    }
    return {
        provinceCode: province.code,
        provinceName: province.name,
        cityName,
        citySlug: cityNameToLocalSeoSlug(cityName),
        category,
    };
}

export function workerJoinLandingPath(resolved: WorkerJoinLandingResolved): string {
    return `${LOCAL_SEO_PATH_PREFIX}/${resolved.provinceCode.toLowerCase()}/${resolved.citySlug}/join/${resolved.category.customerServiceSlug}`;
}

export function resolveWorkerJoinLandingFromPathname(pathname: string): WorkerJoinLandingResolved | null {
    const normalized = pathname.replace(/\/$/, '') || '/';
    const m = normalized.match(/^\/locations\/([a-z]{2})\/([^/]+)\/join\/([^/]+)$/i);
    if (!m) {
        return null;
    }
    return resolveWorkerJoinLanding(m[1], m[2], m[3]);
}

/** Title + meta description for PageMeta / SEO. */
export function getWorkerJoinLandingPageSeo(resolved: WorkerJoinLandingResolved): { title: string; description: string } {
    const { cityName, provinceName, category } = resolved;
    const title = `${localCategoryDisplayName(category)} in ${cityName} — get more jobs | Virtoly`;
    const description = `Join Virtoly for free in ${cityName}, ${provinceName}. Complete your profile, keep 97.5% of your earnings.`;
    return { title, description };
}

/** Title + meta description for PageMeta / SEO. */
export function getLocalLandingPageSeo(resolved: LocalLandingResolved): { title: string; description: string } {
    const enhanced = getEnhancedLocalLandingMeta(resolved);
    if (enhanced) {
        return { title: enhanced.title, description: enhanced.description };
    }

    const { cityName, provinceName, category, subcategory } = resolved;
    if (subcategory) {
        // Service-first titles match how people search and improve CTR vs city-first.
        const title = `${subcategory.name} in ${cityName} | Free Quotes | Virtoly`;
        const description = `Need ${subcategory.name.toLowerCase()} in ${cityName}, ${provinceName}? Compare free quotes from vetted local experts, read reviews, and book trusted help online on Virtoly.`;
        return { title, description };
    }
    const title = `${category.serviceHeading} in ${cityName} | Free Local Quotes | Virtoly`;
    const description = `Book ${category.seoServicePhrase} in ${cityName}, ${provinceName}. Compare free quotes from vetted local experts, read reviews, and schedule trusted help online on Virtoly.`;
    return { title, description };
}

/** Build a resolved object for link helpers (e.g. ServiceAreas). */
export function toLocalLandingResolved(
    provinceCode: string,
    provinceName: string,
    cityName: string,
    category: LocalSeoCategoryDefinition,
    subcategory: { name: string; slug: string } | null,
): LocalLandingResolved {
    return {
        provinceCode,
        provinceName,
        cityName,
        citySlug: cityNameToLocalSeoSlug(cityName),
        category,
        subcategory,
    };
}

/** Local landing → find experts in city with top filters primed (DB category + optional subcategory label). */
export function buildFindExpertsInCityHref(resolved: LocalLandingResolved): string {
    const q = new URLSearchParams();
    q.set('city', resolved.cityName);
    q.set('province', resolved.provinceCode);
    const orderName = serviceOrderCategoryNameForLocalCategory(resolved.category);
    if (orderName) {
        q.set('category', orderName);
    }
    if (resolved.subcategory?.name) {
        q.set('subcategory', resolved.subcategory.name);
    }
    return `/find-experts-in-city?${q.toString()}`;
}

/** Every `/locations/...` URL emitted in the XML sitemap (subset of cities × categories; parity with Laravel). */
export function allLocalLandingPathnames(): string[] {
    const paths: string[] = [];
    for (const province of CANADA_DATA.provinces) {
        for (const city of province.cities) {
            if (!isLocalSeoLandingCity(city)) {
                continue;
            }
            const citySlug = cityNameToLocalSeoSlug(city);
            for (const cat of LOCAL_SEO_CATEGORY_DEFINITIONS) {
                if (!isLocalSeoSitemapCategory(cat)) {
                    continue;
                }
                const base: LocalLandingResolved = {
                    provinceCode: province.code,
                    provinceName: province.name,
                    cityName: city,
                    citySlug,
                    category: cat,
                    subcategory: null,
                };
                paths.push(localLandingPath(base));
                const orderName = serviceOrderCategoryNameForLocalCategory(cat);
                const subs = orderName ? SERVICE_ORDER_SUBCATEGORIES[orderName] : undefined;
                if (subs) {
                    for (const subName of subs) {
                        paths.push(
                            localLandingPath({
                                ...base,
                                subcategory: { name: subName, slug: subcategoryNameToSlug(subName) },
                            }),
                        );
                    }
                }
            }
        }
    }
    return paths;
}

/** Every `/locations/{prov}/{city}/join/{customerSlug}` URL in the XML sitemap (subset of cities × {@link LOCAL_SEO_SITEMAP_CUSTOMER_SERVICE_SLUGS}). */
export function allWorkerJoinLandingPathnames(): string[] {
    const paths: string[] = [];
    for (const province of CANADA_DATA.provinces) {
        for (const city of province.cities) {
            if (!isLocalSeoLandingCity(city)) {
                continue;
            }
            const citySlug = cityNameToLocalSeoSlug(city);
            for (const customerSlug of LOCAL_SEO_SITEMAP_CUSTOMER_SERVICE_SLUGS) {
                paths.push(`${LOCAL_SEO_PATH_PREFIX}/${province.code.toLowerCase()}/${citySlug}/join/${customerSlug}`);
            }
        }
    }
    return paths;
}
